/**
 * Configuration for Supabase and cross-domain authentication
 * Environment variables should be set in your deployment platform
 */

// Supabase Configuration
export const SUPABASE_CONFIG = {
  url:
    import.meta.env.VITE_SUPABASE_URL ||
    "https://rrmhxzaxghiovfqqggfv.supabase.co",
  anonKey:
    import.meta.env.VITE_SUPABASE_ANON_KEY ||
    "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InJybWh4emF4Z2hpb3ZmcXFnZ2Z2Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDE0NDU3NTEsImV4cCI6MjA1NzAyMTc1MX0.yckUWhupNFQf3pEXoDtz0NFK4BmRhuBga2GCAAKlrDg",
};

// Domain Configuration for Cross-Domain Session Sharing
export const DOMAIN_CONFIG = {
  // Parent domain for cookie sharing (must start with dot)
  // In development, use localhost without domain restriction
  parentDomain: import.meta.env.DEV 
    ? undefined // No domain restriction in development
    : import.meta.env.VITE_APP_DOMAIN
    ? `.${import.meta.env.VITE_APP_DOMAIN}`
    : ".world-class.design",

  // Subdomain where authentication happens
  appSubdomain: import.meta.env.VITE_APP_SUBDOMAIN || "app.world-class.design",

  // Main domain for the website
  mainDomain: import.meta.env.VITE_MAIN_DOMAIN || "world-class.design",

  // Subdomain where recruiters browse designers
  recruiterDomain: import.meta.env.VITE_RECRUITER_DOMAIN || "hiring.world-class.design",
};

// Authentication Configuration
export const AUTH_CONFIG = {
  // Where to redirect after OAuth authentication
  redirectTo:
    import.meta.env.VITE_AUTH_REDIRECT_TO ||
    "https://app.world-class.design/auth/callback",

  // Where to redirect authenticated users from main domain
  mainSiteRedirect:
    import.meta.env.VITE_MAIN_SITE_REDIRECT_TO ||
    "https://world-class.design/dashboard",
};

// Cookie Configuration
export const COOKIE_CONFIG = {
  maxAge: parseInt(import.meta.env.VITE_COOKIE_MAX_AGE || "604800"), // 7 days in seconds
  // In development, don't require secure cookies (for localhost)
  secure: import.meta.env.DEV ? false : import.meta.env.VITE_COOKIE_SECURE !== "false",
  sameSite: (import.meta.env.VITE_COOKIE_SAME_SITE || "lax") as
    | "lax"
    | "strict"
    | "none",
  name: "SUPABASE_SESSION",
  crossDomainName: "CROSS_DOMAIN_SESSION",
};

// Environment validation
export function validateEnvironment(): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (!SUPABASE_CONFIG.url) {
    errors.push("VITE_SUPABASE_URL is required");
  }

  if (!SUPABASE_CONFIG.anonKey) {
    errors.push("VITE_SUPABASE_ANON_KEY is required");
  }

  if (!DOMAIN_CONFIG.parentDomain.startsWith(".")) {
    errors.push(
      "Parent domain must start with a dot (e.g., .world-class.design)"
    );
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

// Development mode detection
export const isDevelopment = import.meta.env.DEV;
export const isProduction = import.meta.env.PROD;

// Utility function to get current domain
export function getCurrentDomain(): string {
  if (typeof window === "undefined") return "";
  return window.location.hostname;
}

// Utility function to check if we're on the app subdomain
export function isOnAppSubdomain(): boolean {
  return getCurrentDomain() === DOMAIN_CONFIG.appSubdomain;
}

// Utility function to check if we're on the main domain
export function isOnMainDomain(): boolean {
  return getCurrentDomain() === DOMAIN_CONFIG.mainDomain;
}
